import { Signal }             from "signals"
import _                      from "lodash"
import { observable, action } from "mobx"
import shop2                  from "shop/shop"
import { trackAddToCart }     from "components/tracking/Tracking"

function generateUID() {
  let first  = (Math.random() * 46656) | 0
  let second = (Math.random() * 46656) | 0
  first  = ("000" + first.toString( 36 )).slice( -3 )
  second = ("000" + second.toString( 36 )).slice( -3 )
  return first + second
}

// V2 lists use a distinct prefix so the old ServerStore hydration never
// accidentally picks them up and passes them to the legacy OrderlistService.
const V2_KEY_PREFIX  = "orderlist-V2"
// Old keys written by the legacy service – read during migration.
const OLD_KEY_PREFIX = "list-"

// ─── List Model ──────────────────────────────────────────────────────────────

export class ListModelV2 {
  @observable products = {}
  @observable items    = new Map() // map_id → { map_id, unit_id, unit_multi, count, position }
  @observable hash     = 0
  @observable sum      = 0
  @observable size     = 0

  constructor( { id, title, customName, items = [], extras = {}, isArchived = false } ) {
    this.id         = id
    this.apiKey     = `${V2_KEY_PREFIX}${id}`
    this.title      = title
    // Server converts JSON null to the string "null" — treat both as no custom name.
    this.customName = (customName && customName !== "null") ? customName : null
    this.extras     = extras
    this.isArchived = isArchived
    this.events     = { change: new Signal() }

    // Old format stores items as a plain object {map_id: {...}}, V2 stores them as an array.
    const itemsArray = Array.isArray( items ) ? items : Object.values( items || {} )

    itemsArray.forEach( item => {
      if ( item && item.map_id ) {
        this.items.set( item.map_id, {
          map_id    : item.map_id,
          unit_id   : item.unit_id   || null,
          unit_multi: item.unit_multi || null,
          count     : item.count || 1,
          position  : item.position || Date.now()
        } )
      }
    } )
  }

  // Fetches full product data for all items in this list.
  load() {
    const shop  = shop2
    const tasks = [ ...this.items.values() ].map( item =>
      shop.product( item.map_id )
          .then( product => {
            if ( !product ) {
              this.items.delete( item.map_id )
              return
            }
            product.count      = item.count || 1
            // fallback: if the saved VE no longer exists on the product, keep the
            // default first quantity that ProductModel already selected
            const savedQuantity = item.unit_id && product.quantityById && product.quantityById[ item.unit_id ]
            if ( savedQuantity ) {
              product.unit_id    = item.unit_id
              product.unit_multi = item.unit_multi || savedQuantity.multi
            }
            product.position   = item.position
            this.products[ product.map_id ] = product
          } )
          .catch( () => {
            this.items.delete( item.map_id )
          } )
    )
    this.size = this.items.size
    return Promise.all( tasks ).then( () => {
      this._recalculate()
    } )
  }

  @action
  _recalculate() {
    let price = 0
    let count = 0
    _.forEach( this.products, product => {
      const item = this.items.get( product.map_id )
      if ( !item ) return
      price += product.calculate( item.count || 1 )
      count++
      // Keep item metadata in sync with loaded product
      this.items.set( product.map_id, {
        ...item,
        count     : product.count,
        unit_id   : product.unit_id,
        unit_multi: product.unit_multi
      } )
    } )
    this.sum  = price
    this.size = count
    this.hash++
    this.events.change.dispatch( { list: this } )
  }

  contains( product ) {
    return this.items.has( product.map_id )
  }

  @action
  toggle( product ) {
    if ( this.contains( product ) ) {
      this.remove( product )
    } else {
      this.add( product )
    }
  }

  @action
  add( product = {} ) {
    const { map_id, unit_id, unit_multi, count } = product
    if ( !map_id ) return false

    const old      = this.items.get( map_id )
    const position = (old && old.position) ? old.position : Date.now()
    const qty      = count || 1

    this.items.set( map_id, { map_id, unit_id, unit_multi, count: qty, position } )
    this.hash++

    return shop2.product( map_id ).then( p => {
      if ( !p ) return
      p.count      = qty
      p.unit_multi = unit_multi || p.unit_multi
      p.unit_id    = unit_id    || p.unit_id
      p.position   = position
      this.products[ map_id ] = p
      this._recalculate()
    } )
  }

  @action
  remove( { map_id } ) {
    this.items.delete( map_id )
    delete this.products[ map_id ]
    this._recalculate()
    this.save()
  }

  save() {
    const data = {
      id        : this.id,
      title     : this.title,
      customName: this.customName || undefined, // undefined is omitted by JSON.stringify
      extras    : this.extras,
      isArchived: this.isArchived,
      items     : [ ...this.items.values() ]
    }
    return shop2.store.server.post( this.apiKey, data )
  }

  async toCart( product ) {
    const item    = this.items.get( product.map_id )
    product.count = (item && item.count) || 1
    await shop2.cart.add( product )
  }

  listToCart() {
    _.forEach( this.products, item => {
      this.toCart( item )
      trackAddToCart( item.map_id, item.title )
    } )
  }
}

// ─── Service ─────────────────────────────────────────────────────────────────

export default class OrderlistServiceV2 {
  @observable lists   = {} // { [id]: ListModelV2 }
  @observable listIds = [] // ordered list of IDs

  constructor( opts ) {
    this._shop  = opts.shop
    this.events = { change: new Signal() }
    // memorize=true means this fires immediately if already authenticated
    this._shop.events.authentified.add( this._onLogin )
  }

  _onLogin = () => {
    console.log( "[OrderlistV2] _onLogin fired" )
    this.hydrate()
  }

  async hydrate() {
    console.log( "[OrderlistV2] hydrate() start" )
    try {
      const data = await this._shop.store.server.get()
      console.log( "[OrderlistV2] server.get() result:", data )

      if ( !data || typeof data !== "object" ) {
        console.error( "[OrderlistV2] server.get() returned unexpected value:", data )
        return
      }

      const allKeys   = Object.keys( data )
      const newLists  = {}
      const newIds    = []
      const toMigrate = []

      console.log( "[OrderlistV2] all API keys:", allKeys )

      // ── Load existing V2 lists ───────────────────────────────────────────
      allKeys.forEach( k => {
        const key = decodeURIComponent( k )
        const obj = data[ k ]
        if ( !obj || !key.startsWith( V2_KEY_PREFIX ) ) return
        if ( obj.isArchived ) return
        if ( !obj.id || !obj.title ) return
        console.log( "[OrderlistV2] loading V2 list:", key, obj )
        const list = new ListModelV2( obj )
        list.load()
        newLists[ obj.id ] = list
        newIds.push( obj.id )
      } )

      // ── Collect old-format lists for one-time migration ──────────────────
      allKeys.forEach( k => {
        const key = decodeURIComponent( k )
        const obj = data[ k ]
        if ( !obj || !key.startsWith( OLD_KEY_PREFIX ) ) return
        if ( key.startsWith( V2_KEY_PREFIX ) ) return
        if ( obj.isArchived ) return
        if ( !obj.title ) return
        const alreadyMigrated = Object.values( newLists ).some( l => l.title === obj.title )
        if ( !alreadyMigrated ) {
          console.log( "[OrderlistV2] queuing old list for migration:", key )
          toMigrate.push( { key, obj } )
        }
      } )

      console.log( `[OrderlistV2] V2 lists found: ${newIds.length}, to migrate: ${toMigrate.length}` )

      // ── Migrate old lists → V2 entries ───────────────────────────────────
      for ( const { key, obj } of toMigrate ) {
        const id   = generateUID()
        const list = new ListModelV2( { ...obj, id } )
        list.load()
        newLists[ id ] = list
        newIds.push( id )
        try {
          await list.save()
          console.log( `[OrderlistV2] migrated "${obj.title}" → ${list.apiKey}` )
          // Remove the old-format record now that it's been copied over —
          // otherwise it lingers as an orphaned duplicate forever (e.g. still
          // visible to the mobile app, which only reads legacy "list-" keys).
          await this._shop.store.server.delete( key, {}, obj.etag )
          console.log( `[OrderlistV2] removed legacy key "${key}" after migration` )
        } catch ( err ) {
          console.error( `[OrderlistV2] migration of "${key}" failed`, err )
        }
      }

      this.lists   = newLists
      this.listIds = newIds
      console.log( "[OrderlistV2] hydrate() done, lists:", Object.keys( newLists ) )

      // Guarantee at least one usable list
      if ( newIds.length === 0 ) {
        console.log( "[OrderlistV2] no lists found, creating default Merkliste" )
        this.createList( "Merkliste" )
      }

      this.events.change.dispatch( { lists: this.lists } )
    } catch ( err ) {
      console.error( "[OrderlistV2] hydrate() error:", err )
    }
  }

  // Creates a new list, saves it to the API and notifies observers.
  createList( title, products = [] ) {
    const id   = generateUID()
    const list = new ListModelV2( { id, title } )
    products.forEach( p => list.add( p ) )
    this.lists[ id ] = list
    this.listIds.push( id )
    list.save()
    this.events.change.dispatch( { lists: this.lists } )
    return list
  }

  // Marks a list as archived in the API and removes it from local state.
  removeList( id ) {
    const list = this.lists[ id ]
    if ( !list ) return
    list.isArchived = true
    list.save()
    delete this.lists[ id ]
    this.listIds = this.listIds.filter( lid => lid !== id )
    this.events.change.dispatch( { lists: this.lists } )
  }

  getById( id ) {
    return this.lists[ id ]
  }

  get listsArray() {
    return this.listIds.map( id => this.lists[ id ] ).filter( Boolean )
  }
}



// WEBPACK FOOTER //
// ./src/sdk/OrderlistServiceV2.js